import { PARTNER_TERMS, transferCredits, type PartnerSummary, type PayoutMethod } from '@trendator/shared';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../api';
import { BackIcon } from '../components/icons';
import { plural, rub } from '../format';
import { useBackButton } from '../hooks';
import { haptic, hapticResult } from '../telegram';
import { Loading } from './Status';

type Way = 'balance' | PayoutMethod;

const date = (iso: string) => new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

/** Вывод партнёрских — docs/design/Withdraw.dc.html. */
export function Withdraw({ onBack, onBalance }: { onBack: () => void; onBalance: (balance: number) => void }) {
  useBackButton(onBack);
  const [p, setP] = useState<PartnerSummary | null>(null);
  const [way, setWay] = useState<Way>('balance');
  const [amount, setAmount] = useState('');
  const [phone, setPhone] = useState('');
  const [bank, setBank] = useState('');
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [key, setKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    api
      .partner()
      .then((s) => {
        setP(s);
        setAmount(String(s.available));
      })
      .catch(() => setError('Не получилось загрузить. Проверь интернет.'));
  }, []);

  if (!p) {
    return (
      <main className="screen">
        {error ? <p className="error-text profile__msg">{error}</p> : <Loading />}
      </main>
    );
  }

  const sum = Math.max(0, Math.floor(Number(amount.replace(/\s/g, '')) || 0));
  const min = way === 'balance' ? 1 : PARTNER_TERMS.payout[way].min;
  const soon = way === 'balance' ? null : p.payoutAvailableAt[way];
  const blocked = way !== 'balance' && (Boolean(p.pendingPayout) || Boolean(soon));
  const detailsOk = way === 'balance' || (way === 'sbp' ? phone.replace(/\D/g, '').length >= 10 && bank.trim().length >= 2 : /^T\w{33}$/.test(address.trim()));
  const canSubmit = !busy && !blocked && sum >= min && sum <= p.available && detailsOk;

  const submit = async () => {
    if (!canSubmit) return;
    haptic();
    setBusy(true);
    setError(null);
    try {
      if (way === 'balance') {
        const r = await api.partnerTransfer(sum, key);
        onBalance(r.balance);
        setP(r.partner);
        setDone(`${rub(r.credited)} уже на балансе — можно тратить на тренды.`);
        setKey(crypto.randomUUID());
      } else {
        const r = await api.partnerPayout(way === 'sbp' ? { method: 'sbp', amount: sum, phone, bank } : { method: 'usdt', amount: sum, address: address.trim() });
        setP(r.partner);
        setDone(`Заявка на ${rub(sum)} принята: ${r.payout.destination}. Обычно до 3 дней — напишем в бот.`);
      }
      hapticResult('success');
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      const msg = err instanceof ApiError && typeof err.data.message === 'string' ? err.data.message : null;
      setError(
        code === 'not_enough'
          ? 'Столько пока недоступно — часть ещё в холде.'
          : code === 'too_soon'
            ? 'Вывод — раз в неделю.'
            : code === 'payout_pending'
              ? 'Прошлая заявка ещё в обработке.'
              : (msg ?? 'Не получилось. Попробуй ещё раз.'),
      );
      hapticResult('error');
    } finally {
      setBusy(false);
    }
  };

  const options: { id: Way; title: string; note: string; badge?: string }[] = [
    {
      id: 'balance',
      title: 'На баланс',
      badge: `+${PARTNER_TERMS.toBalanceBonusPercent}%`,
      note: `придёт ${rub(transferCredits(sum || p.available))} · сразу, без минимума`,
    },
    {
      id: 'sbp',
      title: PARTNER_TERMS.payout.sbp.label,
      note: p.payoutAvailableAt.sbp ? `снова с ${date(p.payoutAvailableAt.sbp)}` : `от ${rub(PARTNER_TERMS.payout.sbp.min)} · раз в неделю, ${PARTNER_TERMS.payout.sbp.note}`,
    },
    {
      id: 'usdt',
      title: PARTNER_TERMS.payout.usdt.label,
      note: p.payoutAvailableAt.usdt ? `снова с ${date(p.payoutAvailableAt.usdt)}` : `от ${rub(PARTNER_TERMS.payout.usdt.min)} · ${PARTNER_TERMS.payout.usdt.note}`,
    },
  ];

  return (
    <main className="screen withdraw">
      <header className="withdraw__header">
        <button type="button" className="glass icon-btn" aria-label="Назад" onClick={onBack}>
          <BackIcon />
        </button>
        <h1 className="withdraw__title">Вывод</h1>
      </header>

      <div className="withdraw__total">
        <span className="withdraw__label">Можно вывести</span>
        <span className="withdraw__sum">{rub(p.available)}</span>
        <span className="withdraw__meta">
          {p.bought} {plural(p.bought, 'друг купил', 'друга купили', 'друзей купили')}
          {p.held ? ` · ${rub(p.held)} в холде` : ''}
          {p.lastPayoutAt ? ` · последний вывод ${date(p.lastPayoutAt)}` : ''}
        </span>
      </div>

      <div className="withdraw__ways" role="radiogroup" aria-label="Куда вывести">
        {options.map((o) => (
          <label key={o.id} className={`withdraw__way${way === o.id ? ' withdraw__way--on' : ''}`}>
            <span className="withdraw__way-text">
              <span className="withdraw__way-title">
                {o.title}
                {o.badge ? <span className="withdraw__badge">{o.badge}</span> : null}
              </span>
              <span className="withdraw__way-note">{o.note}</span>
            </span>
            <input
              type="radio"
              name="way"
              checked={way === o.id}
              onChange={() => {
                haptic('select');
                setWay(o.id);
                setError(null);
                setDone(null);
              }}
            />
          </label>
        ))}
      </div>

      {way === 'sbp' ? (
        <div className="glass-list withdraw__details">
          <input aria-label="Телефон" inputMode="tel" placeholder="Телефон, +7 999 123-45-67" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <div className="glass-list__sep withdraw__sep" />
          <input aria-label="Банк" placeholder="Банк, например Т-Банк" value={bank} maxLength={60} onChange={(e) => setBank(e.target.value)} />
        </div>
      ) : null}
      {way === 'usdt' ? (
        <div className="glass-list withdraw__details">
          <input aria-label="Адрес USDT TRC-20" placeholder="Адрес TRC-20, начинается с T" value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>
      ) : null}

      <div className="glass-list withdraw__amount">
        <label htmlFor="sum">Сумма</label>
        <input id="sum" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
        <span>₽</span>
        <button type="button" className="withdraw__all" onClick={() => setAmount(String(p.available))}>
          Всё
        </button>
      </div>

      {p.pendingPayout && way !== 'balance' ? (
        <p className="withdraw__note">Заявка на {rub(p.pendingPayout.amount)} ({p.pendingPayout.destination}) в обработке.</p>
      ) : null}
      {sum > 0 && sum < min ? <p className="withdraw__note">Минимум {rub(min)}.</p> : null}
      {error ? (
        <p className="error-text withdraw__note" role="alert">
          {error}
        </p>
      ) : null}
      {done ? (
        <p className="withdraw__done" role="status">
          {done}
        </p>
      ) : null}

      <div className="result__footer">
        <button type="button" className="btn-accent" disabled={!canSubmit} onClick={() => void submit()}>
          {busy ? 'Отправляем…' : way === 'balance' ? `Зачислить ${rub(transferCredits(sum))} на баланс` : `Вывести ${rub(sum)}`}
        </button>
      </div>
    </main>
  );
}
